import React, { useState, useMemo, useContext } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  Paper,
  Toolbar,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Chip,
  TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";
import CmsContext from "../../store/cms-context";
import { has_admin_access } from "../../Utilities/HelperFunctions";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function ContactsListTable(props) {
  const cmsContext = useContext(CmsContext);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const currentUser = cmsContext.current_user;
  const isAdmin = has_admin_access(currentUser);

  // Apply admin check: only show current user's contacts if not admin
  const visibleContacts = useMemo(() => {
    return isAdmin
      ? props.all_contacts
      : props.all_contacts.filter(contact => contact.email === currentUser.email);
  }, [isAdmin, props.all_contacts, currentUser.email]);

  const selectedContacts = useMemo(
    () => visibleContacts.filter((contact) => contact.is_checked),
    [visibleContacts]
  );

  const filteredContacts = useMemo(
    () =>
      visibleContacts.filter(
        (contact) =>
          contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          contact.phone.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [visibleContacts, searchQuery]
  );

  const handleSelectAllClick = (event) => {
    const isChecked = event.target.checked;
    props.set_all_contacts((prevContacts) =>
      prevContacts.map((contact) =>
        visibleContacts.some(vc => vc.email === contact.email)
          ? { ...contact, is_checked: isChecked }
          : contact
      )
    );
  };

  const handleSelectClick = (email, isChecked) => {
    props.set_all_contacts((prevContacts) =>
      prevContacts.map((contact) =>
        contact.email === email ? { ...contact, is_checked: isChecked } : contact
      )
    );
  };

  const handleEditContact = () => {
    const contactToEdit = selectedContacts[0];
    if (contactToEdit) {
      contactToEdit.request_type = "update";
      props.set_dialog_contact(contactToEdit, true);
    }
  };

  const handleDeleteContacts = () => {
    props.delete_contacts(selectedContacts);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h6" component="div">
            Contacts
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Email or Phone"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mr: 2 }}
          />
          {selectedContacts.length === 1 && (
            <Button onClick={handleEditContact} variant="contained" sx={{ mr: 2 }}>
              Edit
            </Button>
          )}
          {selectedContacts.length > 0 && (
            <Tooltip title="Delete">
              <IconButton onClick={handleDeleteContacts}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
        <TableContainer sx={{ maxHeight: 1000 }}>
          <Table stickyHeader sx={{ minWidth: 750 }} size="medium">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selectedContacts.length > 0 && selectedContacts.length < filteredContacts.length
                    }
                    checked={
                      filteredContacts.length > 0 && selectedContacts.length === filteredContacts.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Notifications</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& tr:nth-of-type(odd)": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
            >
              {filteredContacts
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((contact, index) => {
                  const isChecked = selectedContacts.some(
                    (selectedContact) => selectedContact.email === contact.email
                  );
                  return (
                    <TableRow hover tabIndex={-1} key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isChecked}
                          onChange={(event) =>
                            handleSelectClick(contact.email, event.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell>{contact.email}</TableCell>
                      <TableCell>{contact.phone}</TableCell>
                      <TableCell>
                        {contact.notifications && contact.notifications.length > 0 ? (
                          contact.notifications.map((notification, notifIndex) => (
                            <Box key={notifIndex} sx={{ mb: 1 }}>
                              <Chip
                                label={notification.notificationType || "N/A"}
                                color="primary"
                                sx={{ fontWeight: "bold", mb: 0.5 }}
                              />
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No Notifications
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredContacts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}

export default React.memo(ContactsListTable);
